import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface SelectedAircraftBadgeProps {
  helicopterId?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface HelicopterData {
  id: string;
  manufacturer: string;
  model: string;
  icon_url?: string;
}

export const SelectedAircraftBadge: React.FC<SelectedAircraftBadgeProps> = ({
  helicopterId,
  className,
  size = 'md'
}) => {
  const [helicopter, setHelicopter] = useState<HelicopterData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!helicopterId) {
      setHelicopter(null);
      return;
    }

    const fetchHelicopter = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('helicopters')
          .select('id, manufacturer, model, icon_url')
          .eq('id', helicopterId)
          .single();

        if (error) throw error;
        setHelicopter(data);
      } catch (err) {
        console.error('Error fetching helicopter:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHelicopter();
  }, [helicopterId]);

  if (!helicopterId || loading || !helicopter) {
    return null;
  }

  const sizeClasses = {
    sm: 'h-6 gap-1.5 px-2 text-xs',
    md: 'h-8 gap-2 px-3 text-sm',
    lg: 'h-10 gap-2.5 px-4 text-base'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'flex items-center font-medium border-2 border-primary/30 bg-primary/5',
        sizeClasses[size],
        className
      )}
    >
      {helicopter.icon_url ? (
        <img
          src={`${helicopter.icon_url}?v=${Date.now()}`}
          alt={helicopter.model}
          className={cn('object-contain', iconSizes[size])}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <HelicopterSilhouetteIcon className={iconSizes[size]} tone="nav" />
      )}
      <span className="font-semibold">
        {helicopter.manufacturer} {helicopter.model}
      </span>
    </Badge>
  );
};
